import {
  createCoreRowModel,
  coreFeatures,
  flexRender,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { EmptyState } from '@/components/empty-state'

const features = tableFeatures({
  ...coreFeatures,
  coreRowModel: createCoreRowModel(),
})

export type DataColumn<T extends RowData> = {
  id?: string
  header: ReactNode
  accessorKey?: keyof T & string
  cell?: (row: T) => ReactNode
}

export type DataTablePagination = {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}

type DataTableProps<T extends RowData> = {
  columns: DataColumn<T>[]
  data: T[]
  getRowId?: (row: T) => string
  pagination?: DataTablePagination
  isLoading?: boolean
  isError?: boolean
  errorMessage?: string
  emptyTitle?: string
  emptyBody?: string
  emptyAction?: ReactNode
  onRowClick?: (row: T) => void
}

function toColumnDefs<T extends RowData>(
  columns: DataColumn<T>[],
): ColumnDef<typeof features, T>[] {
  return columns.map((col, i) => {
    const id = col.id ?? col.accessorKey ?? `col-${i}`
    return {
      id,
      accessorKey: col.accessorKey,
      header: () => col.header,
      cell: (ctx) => {
        const row = ctx.row.original as T
        if (col.cell) return col.cell(row)
        if (col.accessorKey) {
          const v = row[col.accessorKey]
          return v == null ? '—' : String(v)
        }
        return null
      },
    } as ColumnDef<typeof features, T>
  })
}

export function DataTable<T extends RowData>({
  columns,
  data,
  getRowId,
  pagination,
  isLoading,
  isError,
  errorMessage = 'Impossible de charger les données.',
  emptyTitle = 'Aucun résultat',
  emptyBody,
  emptyAction,
  onRowClick,
}: DataTableProps<T>) {
  const columnDefs = toColumnDefs(columns)

  const table = useTable({
    features,
    data,
    columns: columnDefs,
    getRowId: getRowId ? (row) => getRowId(row as T) : undefined,
  })

  if (isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">{errorMessage}</p>
      </div>
    )
  }

  if (isLoading) {
    return <p className="text-second-admin">Chargement…</p>
  }

  if (data.length === 0) {
    return <EmptyState title={emptyTitle} body={emptyBody} action={emptyAction} />
  }

  const pageCount = pagination
    ? Math.max(1, Math.ceil(pagination.total / Math.max(pagination.pageSize, 1)))
    : 1
  const page = pagination?.page ?? 1
  const headerGroups = table.getHeaderGroups()
  const rows = table.getRowModel().rows

  return (
    <div className="flex flex-col gap-3">
      <section className="carte-admin overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            {headerGroups.map((hg) => (
              <tr key={hg.id} className="border-b border-bordure-admin">
                {hg.headers.map((h) => (
                  <th key={h.id} className="px-4 py-2.5">
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.map((row) => {
              const cells =
                'getVisibleCells' in row && typeof row.getVisibleCells === 'function'
                  ? row.getVisibleCells()
                  : row.getAllCells()
              return (
                <tr
                  key={row.id}
                  className={`border-b border-bordure-admin last:border-0 ${
                    onRowClick ? 'cursor-pointer hover:bg-gris-fond/40' : ''
                  }`}
                  onClick={onRowClick ? () => onRowClick(row.original as T) : undefined}
                >
                  {cells.map((cell: { id: string; column: { columnDef: { cell?: unknown } }; getContext: () => unknown }) => (
                    <td key={cell.id} className="px-4 py-2.5">
                      {flexRender(cell.column.columnDef.cell as never, cell.getContext() as never)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>

      {pagination && pagination.total > pagination.pageSize ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-second-admin">
          <span>
            {(page - 1) * pagination.pageSize + 1}–
            {Math.min(page * pagination.pageSize, pagination.total)} sur {pagination.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="kicker rounded-pilule border border-bordure-admin px-3 py-1.5 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => pagination.onPageChange(page - 1)}
            >
              Précédent
            </button>
            <span className="tabular-nums">
              {page} / {pageCount}
            </span>
            <button
              type="button"
              className="kicker rounded-pilule border border-bordure-admin px-3 py-1.5 disabled:opacity-40"
              disabled={page >= pageCount}
              onClick={() => pagination.onPageChange(page + 1)}
            >
              Suivant
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
